// reportService.ts
import axios from 'axios';
import { countryConfigs, Country } from '../contexts/LocalizationContext';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3944'; 

export interface TransactionFilters {
  merchant_code?: string;
  currency?: string;
  method?: string;
  action?: string;
  date_from?: string;
  date_to?: string;
  customer_details?: string;
  thirdparty_reference?: string;
  paydrc_reference?: string;
  status?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface TransactionItem {
  id: number;
  merchant_code: string;
  thirdparty_reference: string;
  amount: number;
  currency: string;
  method: string;
  created_at: string;
  updated_at?: string;
  customer_details: string;
  paydrc_reference: string;
  status: string;
  action: string;
  switch_reference?: string;
  telco_reference?: string;
  callback_url?: string;
  status_description?: string;
  status_code?: string;
  status_description_paydrc?: string;
  merchant_comission?: number;
  amount_comission?: number;
  amount_merchant?: number;
  amount_comission_freshpay?: number;
  amount_comission_telco?: number;
  database_source?: string; // Nouveau champ indiquant la source
}

export interface TransactionResponse {
  transactions: TransactionItem[];
  total: number;
  page: number;
  limit: number;
  total_pages: number;
  from_database: {
    paydrc: number;
    reporting: number;
  };
}

export interface HistoricalReportRequest {
  email: string;
  dateFrom: string;
  dateTo: string;
  reportType: 'detailed' | 'summary' | 'analytics';
  includeCharts: boolean;
}

export interface DashboardWeeklyResponse {
  transactions: TransactionItem[];
  total: number;
  page: number;
  limit: number;
  total_pages: number;
  from_database: {
    paydrc: number;
    reporting: number;
  };
}

export interface DashboardStatsResponse {
  period: {
    date_from: string;
    date_to: string;
    days: number;
  };
  paydrc: {
    details?: Array<{
      currency: string;
      status: string;
      count: number;
      total_amount: number;
      avg_amount: number;
    }>;
    error?: string;
  };
  reporting: {
    details?: Array<{
      currency: string;
      status: string;
      count: number;
      total_amount: number;
      avg_amount: number;
    }>;
    error?: string;
  };
  combined: Array<{
    currency: string;
    status: string;
    count: number;
    total_amount: number;
    avg_amount: number;
  }>;
}

export interface DailyStatsResponse {
  period: {
    start_date: string;
    end_date: string;
    days: number;
  };
  daily_stats: Array<{
    date: string;
    total_transactions: number;
    successful_transactions: number;
    failed_transactions: number;
    total_amount: number;
    success_rate: number;
  }>;
}

export interface AnalyticsData {
  trends: Array<{
    metric: string;
    value: string;
    change: string;
  }>;
  statusDistribution: Array<{
    status: string;
    count: number;
    percentage: number;
    color: string;
  }>;
  currencyDistribution: Array<{
    currency: string;
    transactions: number;
    volume: number;
    percentage: number;
    color: string;
  }>;
  methodPerformance: Array<{
    method: string;
    transactions: number;
    volume: number;
    successRate: number;
    trend: 'up' | 'down';
  }>;
  operatorPerformance: Array<{
    name: string;
    transactions: number;
    volumeLocal: number;
    volumeUSD: number;
    successRate: number;
    color: string;
  }>;
  topTransactions: Array<{
    id: number;
    amount: number;
    currency: string;
    status: string;
    method: string;
    created_at: string;
    customer_details: string;
  }>;
  failureReasons: Array<{
    reason: string;
    count: number;
    percentage: number;
    color: string;
  }>;
  // Pour compatibilité avec le backend existant
  timeAnalysis?: Array<any>;
  chartData?: Array<any>;
  dualCurrencyData?: any;
  multiCurrency?: boolean;
  cached?: boolean;
  responseTime?: number;
}

export interface AnalyticsRequest {
  period: 'today' | '7days' | '30days';
  merchant_code?: string;
}

class ReportService {
  private getAuthHeaders() {
    const token = localStorage.getItem('token');
    return {
      Authorization: `Bearer ${token}`,
    };
  }

  // === ENDPOINTS DASHBOARD ===

  /**
   * Récupère les transactions de la dernière semaine (7 jours, aujourd'hui inclus)
   * Endpoint par défaut pour le dashboard
   */
  async getWeeklyTransactions(
    merchantCode?: string,
    page: number = 1,
    limit: number = 50
  ): Promise<DashboardWeeklyResponse> {
    const params = new URLSearchParams();
    if (merchantCode) params.append('merchant_code', merchantCode);
    params.append('page', page.toString());
    params.append('limit', limit.toString());

    const response = await axios.get(
      `${API_URL}/dashboard/transactions/weekly?${params.toString()}`,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  /**
   * Récupère uniquement les transactions d'aujourd'hui
   */
  async getTodayTransactions(
    merchantCode?: string,
    page: number = 1,
    limit: number = 50
  ): Promise<DashboardWeeklyResponse> {
    const params = new URLSearchParams();
    if (merchantCode) params.append('merchant_code', merchantCode);
    params.append('page', page.toString());
    params.append('limit', limit.toString());

    const response = await axios.get(
      `${API_URL}/dashboard/transactions/today?${params.toString()}`,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  /**
   * Récupère uniquement les transactions d'hier
   */
  async getYesterdayTransactions(
    merchantCode?: string,
    page: number = 1,
    limit: number = 50
  ): Promise<DashboardWeeklyResponse> {
    const params = new URLSearchParams();
    if (merchantCode) params.append('merchant_code', merchantCode);
    params.append('page', page.toString());
    params.append('limit', limit.toString());

    const response = await axios.get(
      `${API_URL}/dashboard/transactions/yesterday?${params.toString()}`,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  /**
   * Récupère les statistiques pour la dernière semaine
   */
  async getWeeklyStats(merchantCode?: string): Promise<DashboardStatsResponse> {
    const params = new URLSearchParams();
    if (merchantCode) params.append('merchant_code', merchantCode);

    const response = await axios.get(
      `${API_URL}/dashboard/stats/weekly?${params.toString()}`,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  /**
   * Récupère les statistiques quotidiennes pour X jours
   */
  async getDailyStats(
    merchantCode?: string,
    days: number = 7
  ): Promise<DailyStatsResponse> {
    const params = new URLSearchParams();
    if (merchantCode) params.append('merchant_code', merchantCode);
    params.append('days', days.toString());

    const response = await axios.get(
      `${API_URL}/dashboard/stats/daily?${params.toString()}`,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  // === ENDPOINTS ANALYTICS ===

  /**
   * Récupère les données analytiques pour le dashboard
   */
  async getAnalyticsData(
    period: 'today' | '7days' | '30days' = '7days',
    merchantCode?: string
  ): Promise<AnalyticsData> {
    const params = new URLSearchParams();
    params.append('period', period);
    
    if (merchantCode) {
      params.append('merchant_code', merchantCode);
    }

    console.log('📊 Fetching analytics data from:', `${API_URL}/reports/analytics?${params.toString()}`);

    try {
      const response = await axios.get(
        `${API_URL}/reports/analytics?${params.toString()}`,
        { headers: this.getAuthHeaders() }
      );
      
      console.log('✅ Analytics data received:', response.data);
      return response.data as AnalyticsData;
      
    } catch (error) {
      console.error('❌ Error fetching analytics data:', error);
      
      // En cas d'erreur, retourner des données de démonstration temporaires
      return this.getFallbackAnalyticsData(period);
    }
  }

  /**
   * Données analytiques de fallback (à utiliser temporairement si le backend n'est pas prêt)
   */
  private getCurrentCountryConfig() {
    const savedCountry = localStorage.getItem('moko_country') as Country | null;
    const countryCode = (savedCountry && countryConfigs[savedCountry]) ? savedCountry : 'CD';
    return countryConfigs[countryCode];
  }

  private getFallbackAnalyticsData(period: 'today' | '7days' | '30days'): AnalyticsData {
    console.log('⚠️ Using fallback analytics data for development');

    const multiplier = period === 'today' ? 1 : period === '7days' ? 7 : 30;
    const config = this.getCurrentCountryConfig();
    const primaryCurrency = config.primaryCurrency;
    const operators = config.operators;
    const phonePrefix = config.phonePrefix;

    const operatorColors = ["#10B981", "#EF4444", "#F59E0B", "#8B5CF6", "#3B82F6", "#EC4899"];
    const currencies = config.currencies;

    // Build currency distribution dynamically
    const currencyDistribution = currencies.map((curr, idx) => ({
      currency: curr,
      transactions: Math.round((120 * multiplier) / currencies.length),
      volume: Math.round((15000 * multiplier) / currencies.length),
      percentage: Math.round(100 / currencies.length),
      color: ["#3B82F6", "#8B5CF6", "#10B981", "#F59E0B"][idx % 4]
    }));

    // Build method performance dynamically from operators
    const baseTransactions = [60, 35, 15, 10, 8, 5];
    const methodPerformance = operators.map((op, idx) => ({
      method: op.toLowerCase().replace('-', ''),
      transactions: (baseTransactions[idx] || 5) * multiplier,
      volume: ((baseTransactions[idx] || 5) * 125) * multiplier,
      successRate: 96 - idx * 2,
      trend: (idx < 2 ? "up" : "down") as 'up' | 'down'
    }));

    // Build operator performance dynamically
    const operatorPerformance = operators.map((op, idx) => ({
      name: op,
      transactions: (baseTransactions[idx] || 5) * multiplier,
      volumeLocal: ((baseTransactions[idx] || 5) * 250000) * multiplier,
      volumeUSD: ((baseTransactions[idx] || 5) * 125) * multiplier,
      successRate: 96 - idx * 2,
      color: operatorColors[idx % operatorColors.length]
    }));

    // Build top transactions with country-appropriate phone numbers
    const statuses = ["Successful", "Successful", "Failed", "Successful", "Pending"];
    const topTransactions = operators.slice(0, 5).map((op, idx) => ({
      id: idx + 1,
      amount: [500, 300, 200, 150, 100][idx] || 100,
      currency: idx % 2 === 0 ? "USD" : primaryCurrency,
      status: statuses[idx],
      method: op.toLowerCase().replace('-', ''),
      created_at: new Date().toISOString(),
      customer_details: `+${phonePrefix} ${String(80 + idx).padStart(2, '0')} ${String(Math.floor(Math.random() * 900) + 100)} ${String(Math.floor(Math.random() * 9000) + 1000)}`
    }));

    return {
      trends: [
        { metric: "Volume", value: `${(15000 * multiplier).toLocaleString()} USD`, change: "+12%" },
        { metric: "Transactions", value: `${(120 * multiplier).toLocaleString()}`, change: "+8%" },
        { metric: "Success Rate", value: "95%", change: "+2%" }
      ],
      statusDistribution: [
        { status: "Successful", count: 114 * multiplier, percentage: 95, color: "#10B981" },
        { status: "Failed", count: 4 * multiplier, percentage: 3.3, color: "#EF4444" },
        { status: "Pending", count: 2 * multiplier, percentage: 1.7, color: "#F59E0B" }
      ],
      currencyDistribution,
      methodPerformance,
      operatorPerformance,
      topTransactions,
      failureReasons: [
        { reason: "Insufficient funds", count: 8 * multiplier, percentage: 40, color: "#EF4444" },
        { reason: "Network timeout", count: 6 * multiplier, percentage: 30, color: "#F59E0B" },
        { reason: "Invalid account number", count: 4 * multiplier, percentage: 20, color: "#8B5CF6" },
        { reason: "Daily limit exceeded", count: 2 * multiplier, percentage: 10, color: "#EC4899" }
      ],
      multiCurrency: currencies.length > 1,
      cached: false,
      responseTime: 0.1
    };
  }

  // === ENDPOINTS DE RECHERCHE AVANCÉE ===

  /**
   * Recherche unifiée avec tous les filtres
   */
  async searchTransactions(filters: TransactionFilters): Promise<TransactionResponse> {
    // Nettoyer les filtres vides
    const cleanFilters = Object.fromEntries(
      Object.entries(filters).filter(([_, value]) => 
        value !== undefined && value !== '' && !(Array.isArray(value) && value.length === 0)
      )
    );

    // Debug: voir ce qui est envoyé
    console.log('📤 Sending to /transactions/search with params:', cleanFilters);

    try {
      const response = await axios.get(`${API_URL}/transactions/search`, {
        headers: this.getAuthHeaders(),
        params: cleanFilters,
      });
      
      console.log('✅ Received response from /transactions/search:', {
        page: response.data.page,
        total: response.data.total,
        total_pages: response.data.total_pages,
        transactions_count: response.data.transactions?.length
      });
      
      return response.data;
    } catch (error) {
      console.error('❌ Error in searchTransactions:', error);
      throw error;
    }
  }

  /**
   * Récupère une transaction par référence PayDRC
   */
  async getTransactionByPaydrcReference(reference: string): Promise<TransactionItem> {
    const response = await axios.get(
      `${API_URL}/transactions/${reference}`,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  /**
   * Récupère une transaction par référence tierce
   */
  async getTransactionByThirdpartyReference(reference: string): Promise<TransactionItem> {
    const response = await axios.get(
      `${API_URL}/transactions/thirdparty/${reference}`,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  // === FONCTIONS DE COMPATIBILITÉ (pour l'ancien code) ===

  /**
   * Compatibilité avec l'ancienne fonction getTransactionReport
   * Utilise maintenant le nouveau endpoint de recherche
   */
  async getTransactionReport(oldFilters: any): Promise<TransactionResponse> {
    // Convertir les anciens filtres vers les nouveaux
    const newFilters: TransactionFilters = {
      merchant_code: oldFilters.merchant_code,
      currency: oldFilters.currencies?.[0], // Prend la première devise si multiple
      method: oldFilters.operators?.[0],    // Map operators vers method
      date_from: oldFilters.dateFrom,
      date_to: oldFilters.dateTo,
      status: oldFilters.statuses?.[0],     // Prend le premier statut
      page: oldFilters.page || 1,
      limit: oldFilters.limit || 50,
      // Recherche par texte dans multiple champs
      search: oldFilters.search,
    };

    return this.searchTransactions(newFilters);
  }

  // === FONCTIONS D'EXPORT ===

  /**
   * Exporte les transactions au format Excel via le nouvel endpoint
   */
  // === ASYNC EXPORT FUNCTIONS (PostgreSQL COPY TO) ===

  /**
   * Internal: submit async export, poll until done, download file
   */
  private async _asyncExport(filters: TransactionFilters, format: 'csv' | 'excel'): Promise<void> {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Not authenticated');

    // Build request body
    const body: any = {
      merchant_code: filters.merchant_code || '',
      date_from: filters.date_from || '',
      date_to: filters.date_to || '',
      format: format,
    };
    if (filters.currency) body.currency = filters.currency;
    if (filters.method) body.method = filters.method;
    if (filters.action) body.action = filters.action;
    if (filters.status) body.status = filters.status;

    // Validate dates
    if (!body.date_from || !body.date_to) {
      throw new Error('Please select both start and end dates for export');
    }

    // Step 1: Submit export request
    const submitRes = await fetch(`${API_URL}/async-export/request`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(body)
    });

    if (!submitRes.ok) {
      const err = await submitRes.json().catch(() => ({ detail: 'Export request failed' }));
      throw new Error(err.detail || 'Export request failed');
    }

    const job = await submitRes.json();
    const jobId = job.job_id;
    console.log(`📊 Export job submitted: ${jobId}`);

    // Step 2: Poll until completed or failed
    let status = job.status;
    let attempts = 0;
    const maxAttempts = 600; // 20 min max (2s intervals)
    let lastJob = job;

    while (status === 'queued' || status === 'processing') {
      if (attempts >= maxAttempts) {
        throw new Error('Export timed out. Please try again with a smaller date range.');
      }
      await new Promise(resolve => setTimeout(resolve, 2000));

      const statusRes = await fetch(`${API_URL}/async-export/status/${jobId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!statusRes.ok) {
        attempts++;
        continue;
      }

      lastJob = await statusRes.json();
      status = lastJob.status;
      attempts++;

      if (lastJob.total_rows) {
        const pct = lastJob.progress ? Math.round((lastJob.progress / lastJob.total_rows) * 100) : 0;
        console.log(`📊 Export progress: ${pct}% (${lastJob.progress?.toLocaleString()}/${lastJob.total_rows?.toLocaleString()} rows)`);
      }
    }

    if (status === 'failed') {
      throw new Error(lastJob.error || 'Export failed on server');
    }

    // Step 3: Download the file
    console.log(`📊 Export completed, downloading...`);
    const dlRes = await fetch(`${API_URL}/async-export/download/${jobId}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!dlRes.ok) throw new Error('Download failed');

    const blob = await dlRes.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;

    const ext = format === 'excel' ? 'xlsx' : 'csv';
    const mc = filters.merchant_code || 'export';
    link.download = `transactions_${mc}_${filters.date_from}_${filters.date_to}.${ext}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    const sizeMB = (lastJob.file_size / (1024 * 1024)).toFixed(1);
    console.log(`✅ Export completed: ${lastJob.progress?.toLocaleString()} rows, ${sizeMB} MB`);
  }

  /**
   * Exporte les transactions au format Excel via async PostgreSQL export
   */
  async exportTransactionsToExcel(filters: TransactionFilters): Promise<void> {
    return this._asyncExport(filters, 'excel');
  }

  /**
   * Exporte les transactions au format CSV via async PostgreSQL export
   */
  async exportTransactionsToCSV(filters: TransactionFilters): Promise<void> {
    return this._asyncExport(filters, 'csv');
  }

  private downloadAsExcel(transactions: TransactionItem[]): void {
    console.log('Exporting transactions to Excel:', transactions.length);
    
    // Exemple simple de téléchargement CSV
    const headers = [
      'Merchant Code', 'PayDRC Reference', 'Thirdparty Reference',
      'Created At', 'Amount', 'Currency', 'Method', 'Status', 'Action'
    ];
    
    const csvContent = [
      headers.join(','),
      ...transactions.map(t => [
        t.merchant_code,
        t.paydrc_reference,
        t.thirdparty_reference,
        t.created_at,
        t.amount,
        t.currency,
        t.method,
        t.status,
        t.action
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `transactions_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  /**
   * Demander un rapport historique par email
   */
  async requestHistoricalReport(request: HistoricalReportRequest): Promise<any> {
    try {
      const response = await axios.post(
        `${API_URL}/reports/request-historical-report`,
        request,
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error requesting historical report:', error);
      throw error;
    }
  }

  // === FONCTIONS UTILITAIRES ===

  /**
   * Convertit les anciens opérateurs en méthodes compatibles
   */
  mapOperatorToMethod(operator: string): string {
    const mapping: Record<string, string> = {
      'm-pesa': 'mpesa',
      'mpesa': 'mpesa',
      'airtel': 'airtel',
      'airteltigo': 'airteltigo',
      'orange': 'orange',
      'afrimoney': 'afrimoney',
      'vodacom': 'vodacom',
      'vodafone': 'vodafone',
      'mtn': 'mtn',
      'moov': 'moov',
      'safaricom': 'safaricom',
      'telkom': 'telkom',
      'free': 'free',
      'expresso': 'expresso',
      'malitel': 'malitel',
      'africell': 'africell',
      'tigo': 'tigo',
      'halotel': 'halotel',
      'telecel': 'telecel'
    };
    return mapping[operator.toLowerCase()] || operator.toLowerCase();
  }

  /**
   * Convertit les anciens statuts en nouveaux statuts
   */
  mapOldStatusToNew(status: string): string {
    const mapping: Record<string, string> = {
      'Successful': 'SUCCESS',
      'Failed': 'FAILED',
      'Pending': 'PENDING',
      'Submitted': 'SUBMITTED'
    };
    return mapping[status] || status.toUpperCase();
  }

  /**
   * Méthode utilitaire pour obtenir le merchant_code depuis le token
   */
  private getMerchantCodeFromToken(): string | null {
    try {
      const token = localStorage.getItem('token');
      if (!token) return null;
      
      // Décodez le token JWT pour obtenir le merchant_code
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.merchant_code || null;
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  }
}

export const reportService = new ReportService();